import * as React from 'react'

import { Label } from '@ui/components/ui/label'
import { cn } from '@ui/lib/utils'

export interface FormFieldProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'id'> {
  /** Beschriftung des Feldes. */
  label: React.ReactNode
  /** id des Eingabefeldes — muss mit der id am Control übereinstimmen. */
  htmlFor: string
  /** Fehlertext aus der Validierung. Wird rot und als Meldung ausgegeben. */
  error?: string
  /** Erklärung unter dem Feld, z.B. erwartetes Format. */
  description?: string
  /** Markiert das Feld sichtbar und für Screenreader als Pflichtfeld. */
  required?: boolean
}

/**
 * Rahmen für ein Formularfeld: Beschriftung, Control, Erklärung und
 * Fehlermeldung — inklusive der aria-Verknüpfungen, die man sonst vergisst.
 *
 * Das Control selbst kommt als Kind. Die Feldbindung macht das Modul mit
 * react-hook-form; dieser Baustein weiss davon nichts.
 *
 * @example
 * ```tsx
 * <FormField
 *   label="Titel"
 *   htmlFor="titel"
 *   required
 *   error={form.formState.errors.titel?.message}
 *   description="Kurz und eindeutig, maximal 80 Zeichen."
 * >
 *   <Input id="titel" aria-invalid={!!form.formState.errors.titel} {...form.register('titel')} />
 * </FormField>
 * ```
 */
export function FormField({
  label,
  htmlFor,
  error,
  description,
  required,
  className,
  children,
  ...props
}: FormFieldProps) {
  const descriptionId = description ? `${htmlFor}-description` : undefined
  const errorId = error ? `${htmlFor}-error` : undefined

  return (
    <div className={cn('space-y-2', className)} {...props}>
      <Label htmlFor={htmlFor} className={cn(error && 'text-destructive-strong')}>
        {label}
        {required && (
          <span className="text-destructive-strong" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only">(Pflichtfeld)</span>}
      </Label>

      <FormFieldContext value={{ describedBy: cn(descriptionId, errorId) || undefined }}>
        {children}
      </FormFieldContext>

      {description && (
        <p id={descriptionId} className="text-sm text-muted-foreground">
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-sm font-medium text-destructive-strong">
          {error}
        </p>
      )}
    </div>
  )
}

interface FormFieldContextValue {
  describedBy: string | undefined
}

const Context = React.createContext<FormFieldContextValue | null>(null)

function FormFieldContext({
  value,
  children,
}: {
  value: FormFieldContextValue
  children: React.ReactNode
}) {
  return <Context value={value}>{children}</Context>
}

/**
 * Liefert die aria-describedby-Verknüpfung des umgebenden {@link FormField}.
 * Nützlich für eigene Controls, die kein Input sind.
 *
 * @example
 * ```tsx
 * const { describedBy } = useFormFieldAria()
 * <MeinControl aria-describedby={describedBy} />
 * ```
 */
export function useFormFieldAria(): FormFieldContextValue {
  return React.useContext(Context) ?? { describedBy: undefined }
}
